import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Share2, Trash2, X } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { colors, inr } from "@/theme";
import { Spinner, EmptyState } from "@/components/common";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toast } from "sonner";

export default function ShortlistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sl, setSl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(() => api(`/shortlists/${id}`).then(setSl).catch(() => setNotFound(true)), [id]);
  useEffect(() => { load(); }, [load]);

  const share = () => {
    const url = `${window.location.origin}/shortlist/share/${sl.share_slug}`;
    if (navigator.share) navigator.share({ title: sl.name, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Share link copied"); }
  };
  const removeItem = async (pid) => {
    try { const u = await api(`/shortlists/${id}/items/${pid}`, { method: "DELETE" }); setSl(u); } catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    if (!window.confirm("Delete this Nest Table?")) return;
    try { await api(`/shortlists/${id}`, { method: "DELETE" }); toast("Deleted"); navigate("/shortlists"); } catch (e) { toast.error(e.message); }
  };

  if (notFound) return <EmptyState title="Nest Table not found" action={<Link to="/shortlists" className="btn-primary" style={{ textDecoration: "none" }}>My Tables</Link>} />;
  if (!sl) return <Spinner full />;

  return (
    <div className="container-max fade-up" style={{ padding: isMobile ? "24px 16px 40px" : "40px 24px 56px", maxWidth: 1000 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}><Link to="/shortlists" style={{ color: colors.mutedText, textDecoration: "none" }}>Nest Tables</Link> / {sl.name}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="font-display" style={{ fontSize: 34, fontWeight: 700, margin: 0 }}>{sl.name}</h1>
          <p className="font-body" style={{ color: colors.mutedText, marginTop: 4 }}>{sl.count} item{sl.count !== 1 ? "s" : ""}{sl.occasion ? ` · ${sl.occasion}` : ""}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button data-testid="share-shortlist" className="btn-brand" onClick={share}><Share2 size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />Share</button>
          <button data-testid="delete-shortlist" className="btn-outline" onClick={del} style={{ padding: 13 }}><Trash2 size={16} /></button>
        </div>
      </div>

      {sl.products.length === 0 ? (
        <EmptyState title="Empty table" message="Add pieces from any product page via 'Add to Nest Table'." action={<Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Browse Shop</Link>} />
      ) : (
        <div className="product-grid" style={{ marginTop: 24 }}>
          {sl.products.map((p) => (
            <div key={p.id} style={{ position: "relative" }}>
              <button data-testid={`remove-item-${p.id}`} onClick={() => removeItem(p.id)} style={{ position: "absolute", top: 8, right: 8, zIndex: 2, width: 30, height: 30, borderRadius: 999, background: "rgba(252,251,248,0.92)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={15} /></button>
              <Link to={`/product/${p.id}`} style={{ textDecoration: "none", color: colors.onSurface }}>
                <div style={{ aspectRatio: "3 / 4", overflow: "hidden", border: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ padding: "10px 2px" }}>
                  <div className="font-body clamp-1" style={{ fontWeight: 500 }}>{p.name}</div>
                  <div className="font-body" style={{ fontWeight: 600, marginTop: 3 }}>{inr(p.price)}</div>
                  {p.bought && <span style={{ fontSize: 11, color: colors.brand, fontWeight: 700 }}>✓ Gifted</span>}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
